import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input } from '../../components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/Tabs';
import {
    Users, FileText, Settings, Search, Plus, Filter, MoreVertical, X,
    ChevronDown, Check, UserX, Eye, ChevronLeft, ChevronRight, UserCheck,
    Stethoscope, GraduationCap, User, HeartPulse, Shield
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import MISReports from './MISReports';

const PAGE_SIZE = 10;

// Role → badge variant mapping
const ROLE_VARIANT = {
    'Therapist': 'primary',
    'Therapist / Educator': 'primary',
    'Special Educator': 'secondary',
    'Administrator': 'default',
    'Admin': 'default',
    'Parent': 'warning',
    'Patient': 'success',
    'Patient / Self-Advocate': 'success',
};

// Role → icon mapping
const ROLE_ICON = {
    'Therapist': Stethoscope,
    'Therapist / Educator': Stethoscope,
    'Special Educator': GraduationCap,
    'Administrator': Shield,
    'Admin': Shield,
    'Parent': HeartPulse,
    'Patient': User,
    'Patient / Self-Advocate': User,
};

// Role group tabs for overview
const ROLE_GROUPS = [
    { label: 'All Accounts', key: 'all' },
    { label: 'Therapists', key: 'therapist' },
    { label: 'Special Educators', key: 'educator' },
    { label: 'Patients', key: 'patient' },
    { label: 'Parents', key: 'parent' },
    { label: 'Admins', key: 'admin' },
];

function matchesRoleGroup(user, key) {
    const r = (user.role || '').toLowerCase();
    if (key === 'all') return true;
    if (key === 'therapist') return r.includes('therapist');
    if (key === 'educator') return r.includes('educator');
    if (key === 'patient') return r.includes('patient') || r.includes('self-advocate');
    if (key === 'parent') return r.includes('parent');
    if (key === 'admin') return r.includes('admin');
    return true;
}

export default function Admin() {
    const { registeredAccounts, addUser, updateAccountStatus, adminEvaluations, addAdminEvaluation, updateAdminEvaluation } = useData();

    const [isInviteModalOpen, setInviteModalOpen] = useState(false);
    const [isTemplateModalOpen, setTemplateModalOpen] = useState(false);
    const [editingTemplateId, setEditingTemplateId] = useState(null);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [createTemplateTitle, setCreateTemplateTitle] = useState('');
    const [createTemplateDuration, setCreateTemplateDuration] = useState('');
    const [createTemplateType, setCreateTemplateType] = useState('Screening');
    const [createTemplateQuestions, setCreateTemplateQuestions] = useState(['']);
    const [searchQuery, setSearchQuery] = useState('');
    const [roleGroupTab, setRoleGroupTab] = useState('all');
    const [filterOpen, setFilterOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [actionMenuId, setActionMenuId] = useState(null);
    const [viewUser, setViewUser] = useState(null);
    const filterRef = useRef(null);

    useEffect(() => {
        const handler = (e) => {
            if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleInvite = (e) => {
        e.preventDefault();
        const data = new FormData(e.target);
        addUser({ name: data.get('name'), email: data.get('email'), role: data.get('role') });
        setInviteModalOpen(false);
        e.target.reset();
    };

    const handleAddTemplateQuestion = () => setCreateTemplateQuestions(prev => [...prev, '']);
    const handleRemoveTemplateQuestion = (idx) => setCreateTemplateQuestions(prev => prev.filter((_, i) => i !== idx));
    const handleTemplateQuestionChange = (idx, val) => setCreateTemplateQuestions(prev => prev.map((q, i) => i === idx ? val : q));

    const openEditTemplate = (template) => {
        setEditingTemplateId(template.id);
        setCreateTemplateTitle(template.title);
        setCreateTemplateDuration(template.duration);
        setCreateTemplateType(template.type || 'Screening');
        
        let qs = [];
        template.template?.sections?.forEach(s => {
            s.questions?.forEach(q => qs.push(q.label));
        });
        if (qs.length === 0) qs = [''];
        setCreateTemplateQuestions(qs);
        setTemplateModalOpen(true);
    };

    const handleCreateTemplateSubmit = (e) => {
        e.preventDefault();
        if (!createTemplateTitle.trim()) return alert('Please enter a title.');
        const validQuestions = createTemplateQuestions.filter(q => q.trim());
        if (validQuestions.length < 1) return alert('Please add at least one question.');
        
        const tmplData = {
            title: createTemplateTitle.trim(),
            duration: createTemplateDuration.trim() || '~15 mins',
            type: createTemplateType,
            domains: validQuestions.length,
            template: {
                id: editingTemplateId ? adminEvaluations.find(ev => ev.id === editingTemplateId)?.template?.id || `tmpl-${Date.now()}` : `tmpl-${Date.now()}`,
                title: createTemplateTitle.trim(),
                duration: createTemplateDuration.trim() || '~15 mins',
                domains: validQuestions.length,
                sections: [{ title: 'Main Section', questions: validQuestions.map((q, i) => ({ id: `q${i}`, label: q, type: 'scale' })) }]
            }
        };

        if (editingTemplateId) {
            updateAdminEvaluation({ ...tmplData, id: editingTemplateId });
        } else {
            addAdminEvaluation(tmplData);
        }

        setTemplateModalOpen(false);
        setEditingTemplateId(null);
        setCreateTemplateTitle('');
        setCreateTemplateDuration('');
        setCreateTemplateQuestions(['']);
        setCreateTemplateType('Screening');
    };

    const filtered = useMemo(() => {
        return registeredAccounts.filter(u => {
            const matchesSearch = searchQuery === '' ||
                u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (u.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
                (u.role || '').toLowerCase().includes(searchQuery.toLowerCase());
            return matchesSearch && matchesRoleGroup(u, roleGroupTab);
        });
    }, [registeredAccounts, searchQuery, roleGroupTab]);

    // Pagination
    const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
    const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
    useEffect(() => { setPage(1); }, [searchQuery, roleGroupTab]);

    // Stats
    const activeCount = registeredAccounts.filter(u => u.status === 'Active').length;
    const inactiveCount = registeredAccounts.length - activeCount;
    const therapistCount = registeredAccounts.filter(u => matchesRoleGroup(u, 'therapist') || matchesRoleGroup(u, 'educator')).length;
    const patientCount = registeredAccounts.filter(u => matchesRoleGroup(u, 'patient')).length;

    const RoleIcon = (role) => {
        const Icon = ROLE_ICON[role] || User;
        return <Icon className="h-4 w-4" />;
    };

    return (
        <div className="max-w-7xl mx-auto space-y-6 pb-12 relative">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900">Administration</h1>
                    <p className="text-sm text-slate-500">Manage all institution accounts, templates, and global settings.</p>
                </div>
            </div>

            {/* Stats Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="shadow-sm border-l-4 border-l-brand-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Total Accounts</p>
                            <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{registeredAccounts.length}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-brand-50 flex items-center justify-center text-brand-600"><Users className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
                <Card className="shadow-sm border-l-4 border-l-green-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Active</p>
                            <h3 className="text-2xl font-bold text-green-600 mt-0.5">{activeCount}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-green-50 flex items-center justify-center text-green-600"><UserCheck className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
                <Card className="shadow-sm border-l-4 border-l-teal-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Therapists / Educators</p>
                            <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{therapistCount}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600"><Stethoscope className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
                <Card className="shadow-sm border-l-4 border-l-violet-500">
                    <CardContent className="p-4 flex items-center justify-between">
                        <div>
                            <p className="text-xs text-slate-500 font-medium">Patients / Parents</p>
                            <h3 className="text-2xl font-bold text-slate-900 mt-0.5">{patientCount}</h3>
                        </div>
                        <div className="h-9 w-9 rounded-lg bg-violet-50 flex items-center justify-center text-violet-600"><User className="h-5 w-5" /></div>
                    </CardContent>
                </Card>
            </div>

            <Tabs defaultValue="users">
                <TabsList className="mb-6 bg-transparent p-0 gap-2 border-b border-slate-200 rounded-none w-full justify-start overflow-x-auto">
                    <TabsTrigger value="users" className="border-b-2 border-transparent data-[state=active]:border-brand-600 rounded-none bg-transparent hover:bg-slate-50 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-brand-600">
                        User Management
                    </TabsTrigger>
                    <TabsTrigger value="templates" className="border-b-2 border-transparent data-[state=active]:border-brand-600 rounded-none bg-transparent hover:bg-slate-50 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-brand-600">
                        Assessment Templates
                    </TabsTrigger>
                    <TabsTrigger value="mis-reports" className="border-b-2 border-transparent data-[state=active]:border-brand-600 rounded-none bg-transparent hover:bg-slate-50 data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:text-brand-600">
                        MIS Reports
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="users" className="space-y-4">
                    {/* Role Group Tabs */}
                    <div className="flex gap-2 overflow-x-auto pb-1 flex-wrap">
                        {ROLE_GROUPS.map(rg => (
                            <button
                                key={rg.key}
                                onClick={() => setRoleGroupTab(rg.key)}
                                className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${roleGroupTab === rg.key
                                    ? 'bg-brand-600 text-white border-brand-600 shadow-sm'
                                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
                            >
                                {rg.label}
                                <span className={`ml-1.5 text-[10px] font-bold ${roleGroupTab === rg.key ? 'text-brand-200' : 'text-slate-400'}`}>
                                    {registeredAccounts.filter(u => matchesRoleGroup(u, rg.key)).length}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Search + action toolbar */}
                    <div className="flex flex-col sm:flex-row gap-4 justify-between bg-white p-4 items-center rounded-xl border border-slate-200 shadow-sm">
                        <div className="relative flex-1 w-full max-w-sm">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                            <Input className="pl-9" placeholder="Search by name, email or role..."
                                value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
                        </div>
                        <div className="flex items-center gap-3 w-full sm:w-auto">
                            <Button
                                variant="outline"
                                className="flex-1 sm:flex-none"
                                onClick={() => setSearchQuery('')}
                                disabled={searchQuery === ''}
                            >
                                <X className="mr-2 h-4 w-4" /> Clear Filter
                            </Button>
                            <Button className="flex-1 sm:flex-none" onClick={() => setInviteModalOpen(true)}>
                                <Plus className="mr-2 h-4 w-4" /> Invite User
                            </Button>
                        </div>
                    </div>

                    <Card className="overflow-hidden shadow-sm">
                        <div className="overflow-x-auto w-full">
                            <table className="w-full text-sm text-left text-slate-600 whitespace-nowrap">
                                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                                    <tr>
                                        <th className="px-6 py-4 font-semibold">User</th>
                                        <th className="px-6 py-4 font-semibold">Role</th>
                                        <th className="px-6 py-4 font-semibold">Status</th>
                                        <th className="px-6 py-4 font-semibold">Date Added</th>
                                        <th className="px-6 py-4 text-right font-semibold">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 bg-white">
                                    {paginated.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="px-6 py-14 text-center text-slate-400 text-sm">
                                                <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                                                No accounts found{searchQuery ? ` for "${searchQuery}"` : ''}.
                                            </td>
                                        </tr>
                                    ) : paginated.map((user) => {
                                        const Icon = ROLE_ICON[user.role] || User;
                                        return (
                                            <tr key={user.id ?? user.email} className="hover:bg-slate-50/60 transition-colors">
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-3">
                                                        <div className="h-9 w-9 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-xs shrink-0">
                                                            {(user.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                                        </div>
                                                        <div>
                                                            <p className="font-semibold text-slate-900">{user.name}</p>
                                                            <p className="text-xs text-slate-500">{user.email}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-1.5">
                                                        <Icon className="h-3.5 w-3.5 text-slate-400" />
                                                        <Badge variant={ROLE_VARIANT[user.role] || 'outline'}>
                                                            {user.role}
                                                        </Badge>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4">
                                                    <div className="flex items-center gap-1.5">
                                                        <span className={`h-1.5 w-1.5 rounded-full ${user.status === 'Active' ? 'bg-green-500' : 'bg-slate-300'}`} />
                                                        <Badge variant={user.status === 'Active' ? 'success' : 'warning'}>
                                                            {user.status || 'Active'}
                                                        </Badge>
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 text-slate-500">{user.added || '—'}</td>
                                                <td className="px-6 py-4 text-right">
                                                    <div className="relative inline-block">
                                                        <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-900"
                                                            onClick={() => setActionMenuId(actionMenuId === (user.id ?? user.email) ? null : (user.id ?? user.email))}>
                                                            <MoreVertical className="h-4 w-4" />
                                                        </Button>
                                                        {actionMenuId === (user.id ?? user.email) && (
                                                            <div className="absolute right-0 top-full mt-1 w-44 bg-white rounded-xl border border-slate-200 shadow-xl z-50 py-1.5">
                                                                <button className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2 text-slate-700"
                                                                    onClick={() => { setViewUser(user); setActionMenuId(null); }}>
                                                                    <Eye className="h-4 w-4 text-slate-400" /> View Profile
                                                                </button>
                                                                <button
                                                                    className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center gap-2"
                                                                    onClick={() => { updateAccountStatus(user.email, user.status === 'Active' ? 'Inactive' : 'Active'); setActionMenuId(null); }}>
                                                                    <UserX className={`h-4 w-4 ${user.status === 'Active' ? 'text-red-400' : 'text-green-500'}`} />
                                                                    <span className={user.status === 'Active' ? 'text-red-600' : 'text-green-600'}>
                                                                        {user.status === 'Active' ? 'Deactivate' : 'Reactivate'}
                                                                    </span>
                                                                </button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="p-4 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500 bg-slate-50">
                            <span>
                                {filtered.length === 0 ? 'No results' : `Showing ${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, filtered.length)} of ${filtered.length}`}
                            </span>
                            <div className="flex gap-1">
                                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="disabled:opacity-40">
                                    <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                                </Button>
                                <span className="flex items-center px-2 text-xs font-semibold text-slate-600">{page} / {totalPages}</span>
                                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} className="disabled:opacity-40">
                                    Next <ChevronRight className="h-4 w-4 ml-1" />
                                </Button>
                            </div>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="templates" className="space-y-4">
                    <div className="flex justify-between items-center bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                        <div>
                            <h2 className="text-lg font-bold text-slate-900">Assessment Templates</h2>
                            <p className="text-sm text-slate-500">Manage templates available across the institution.</p>
                        </div>
                        <Button onClick={() => { setEditingTemplateId(null); setCreateTemplateTitle(''); setCreateTemplateDuration(''); setCreateTemplateQuestions(['']); setTemplateModalOpen(true); }}>
                            <Plus className="mr-2 h-4 w-4" /> Create New Template
                        </Button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {adminEvaluations.map(evalItem => (
                            <Card key={evalItem.id} className="shadow-sm hover:shadow-md transition-shadow">
                                <CardContent className="p-6">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="h-12 w-12 bg-slate-50 rounded-xl flex items-center justify-center text-brand-600 border border-slate-100">
                                            <FileText className="h-6 w-6" />
                                        </div>
                                        <Badge variant="outline">{evalItem.type || 'Standard'}</Badge>
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900 mb-1">{evalItem.title}</h3>
                                    <p className="text-sm text-slate-500 mb-4">{evalItem.domains} Domains • {evalItem.duration}</p>
                                    <div className="flex gap-2">
                                        <Button variant="outline" className="flex-1 text-xs" size="sm" onClick={() => openEditTemplate(evalItem)}>Edit</Button>
                                        <Button variant="outline" className="flex-1 text-xs" size="sm" onClick={() => setPreviewTemplate(evalItem)}>Preview</Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="mis-reports">
                    <MISReports />
                </TabsContent>
            </Tabs>

            {/* Click-outside overlay to close menus */}
            {actionMenuId && (
                <div className="fixed inset-0 z-40" onClick={() => setActionMenuId(null)} />
            )}

            {/* View Profile Modal */}
            {viewUser && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <Card className="w-full max-w-sm shadow-xl border-0">
                        <div className="p-6 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h2 className="text-lg font-bold text-slate-900">User Profile</h2>
                            <Button variant="ghost" size="icon" onClick={() => setViewUser(null)} className="h-8 w-8"><X className="h-4 w-4" /></Button>
                        </div>
                        <CardContent className="p-6 space-y-4">
                            <div className="flex items-center gap-4">
                                <div className="h-14 w-14 rounded-full bg-brand-100 flex items-center justify-center text-brand-700 font-bold text-lg">
                                    {(viewUser.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                                </div>
                                <div>
                                    <p className="font-bold text-slate-900 text-lg">{viewUser.name}</p>
                                    <Badge variant={ROLE_VARIANT[viewUser.role] || 'outline'}>{viewUser.role}</Badge>
                                </div>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {[
                                    { label: 'Email', value: viewUser.email || '—' },
                                    { label: 'Status', value: viewUser.status || 'Active' },
                                    { label: 'Date Added', value: viewUser.added || '—' },
                                ].map(row => (
                                    <div key={row.label} className="flex justify-between items-center py-3">
                                        <span className="text-sm text-slate-500">{row.label}</span>
                                        <span className="text-sm font-semibold text-slate-900">{row.value}</span>
                                    </div>
                                ))}
                            </div>
                            <div className="flex gap-3 pt-2">
                                <Button variant="outline" className="flex-1" onClick={() => setViewUser(null)}>Close</Button>
                                <Button
                                    className={`flex-1 ${viewUser.status === 'Active' ? 'bg-red-500 hover:bg-red-600' : 'bg-green-600 hover:bg-green-700'}`}
                                    onClick={() => {
                                        updateAccountStatus(viewUser.email, viewUser.status === 'Active' ? 'Inactive' : 'Active');
                                        setViewUser(v => ({ ...v, status: v.status === 'Active' ? 'Inactive' : 'Active' }));
                                    }}
                                >
                                    <UserX className="mr-2 h-4 w-4" />
                                    {viewUser.status === 'Active' ? 'Deactivate' : 'Reactivate'}
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Invite Modal */}
            {isInviteModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <Card className="w-full max-w-md shadow-xl border-0">
                        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 pb-4">
                            <CardTitle>Invite New User</CardTitle>
                            <Button variant="ghost" size="icon" onClick={() => setInviteModalOpen(false)} className="h-8 w-8 -mr-2"><X className="h-4 w-4" /></Button>
                        </CardHeader>
                        <form onSubmit={handleInvite}>
                            <CardContent className="space-y-4 pt-6">
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-1">Full Name</label>
                                    <Input name="name" required placeholder="e.g. John Doe" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-1">Email Address</label>
                                    <Input name="email" type="email" required placeholder="john@example.com" />
                                </div>
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-1">Role</label>
                                    <select name="role" className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500">
                                        <option value="Therapist">Therapist</option>
                                        <option value="Special Educator">Special Educator</option>
                                        <option value="Administrator">Administrator</option>
                                        <option value="Parent">Parent/Caregiver</option>
                                        <option value="Patient">Patient</option>
                                    </select>
                                </div>
                            </CardContent>
                            <div className="p-6 pt-0 flex gap-3 mt-2">
                                <Button type="button" variant="outline" className="flex-1" onClick={() => setInviteModalOpen(false)}>Cancel</Button>
                                <Button type="submit" className="flex-1">Send Invitation</Button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}

            {/* Create Template Modal */}
            {isTemplateModalOpen && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <Card className="w-full max-w-lg shadow-xl border-0 max-h-[90vh] flex flex-col">
                        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 pb-4 shrink-0">
                            <CardTitle>Create New Template</CardTitle>
                            <Button variant="ghost" size="icon" onClick={() => setTemplateModalOpen(false)} className="h-8 w-8 -mr-2"><X className="h-4 w-4" /></Button>
                        </CardHeader>
                        <form onSubmit={handleCreateTemplateSubmit} className="flex flex-col flex-1 overflow-hidden">
                            <CardContent className="space-y-4 pt-6 overflow-y-auto">
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-1">Template Title</label>
                                    <Input required placeholder="e.g. Behavioral Screening" value={createTemplateTitle} onChange={e => setCreateTemplateTitle(e.target.value)} />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-slate-700 block mb-1">Duration</label>
                                        <Input placeholder="e.g. ~15 mins" value={createTemplateDuration} onChange={e => setCreateTemplateDuration(e.target.value)} />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-slate-700 block mb-1">Type</label>
                                        <select className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500" value={createTemplateType} onChange={e => setCreateTemplateType(e.target.value)}>
                                            <option value="Screening">Screening</option>
                                            <option value="Baseline">Baseline</option>
                                            <option value="Questionnaire">Questionnaire</option>
                                        </select>
                                    </div>
                                </div>
                                
                                <div>
                                    <label className="text-sm font-medium text-slate-700 block mb-2 mt-4">Questions</label>
                                    <div className="space-y-3">
                                        {createTemplateQuestions.map((q, idx) => (
                                            <div key={idx} className="flex items-start gap-2">
                                                <span className="text-slate-400 font-medium text-sm mt-2.5 w-4">{idx + 1}.</span>
                                                <Input
                                                    className="flex-1"
                                                    placeholder="Enter question text..."
                                                    value={q}
                                                    onChange={(e) => handleTemplateQuestionChange(idx, e.target.value)}
                                                    required
                                                />
                                                <Button type="button" variant="ghost" size="icon" className="text-red-400 hover:text-red-500 shrink-0" onClick={() => handleRemoveTemplateQuestion(idx)} disabled={createTemplateQuestions.length === 1}>
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                    <Button type="button" variant="outline" className="w-full mt-3 border-dashed" onClick={handleAddTemplateQuestion}>
                                        <Plus className="mr-2 h-4 w-4" /> Add Question
                                    </Button>
                                </div>
                            </CardContent>
                            <div className="p-6 pt-4 flex gap-3 border-t border-slate-100 shrink-0">
                                <Button type="button" variant="outline" className="flex-1" onClick={() => setTemplateModalOpen(false)}>Cancel</Button>
                                <Button type="submit" className="flex-1">Create Template</Button>
                            </div>
                        </form>
                    </Card>
                </div>
            )}

            {/* Preview Modal */}
            {previewTemplate && (
                <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
                    <Card className="w-full max-w-2xl shadow-xl border-0 max-h-[90vh] flex flex-col">
                        <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 pb-4 shrink-0">
                            <div>
                                <CardTitle>{previewTemplate.title}</CardTitle>
                                <p className="text-xs text-slate-500 mt-1">{previewTemplate.type} • {previewTemplate.duration}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setPreviewTemplate(null)} className="h-8 w-8 -mr-2"><X className="h-4 w-4" /></Button>
                        </CardHeader>
                        <CardContent className="space-y-6 pt-6 overflow-y-auto">
                            {previewTemplate.template?.sections?.map((section, sIdx) => (
                                <div key={sIdx}>
                                    <h3 className="font-bold text-slate-800 mb-3">{section.title}</h3>
                                    <ul className="space-y-3">
                                        {section.questions?.map((q, qIdx) => (
                                            <li key={qIdx} className="flex gap-3 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg border border-slate-100">
                                                <span className="font-bold text-brand-600">{qIdx + 1}.</span>
                                                <div>
                                                    <p>{q.label}</p>
                                                    {q.options && <p className="text-xs text-slate-400 mt-1">Options: {q.options.join(', ')}</p>}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
